import { useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'

import {
  isAnalyticsConsented,
  whenAnalyticsConsented,
} from '~publish/helpers/publicCookieConsent'
import { runWhenActivated } from '~publish/helpers/runWhenActivated'
import {
  clearPublicTracking,
  withdrawPublicTracking,
  trackPublicSurface,
  trackPublicCtaViewed,
  getPublicActionSessionId,
} from '~publish/helpers/publicTracking'
import type {
  PublicSurface,
  PublicSurfaceState,
} from '~publish/helpers/publicTracking'

const ConsentUpdate = z.object({ rejected: z.array(z.string()).optional() })
const NO_CTAS: readonly string[] = []

export function usePublicTracking(
  surface: PublicSurface | undefined,
  surfaceState: PublicSurfaceState,
  visibleCtas: readonly string[] = NO_CTAS,
): void {
  const current = useRef({ surface, surfaceState, visibleCtas })
  current.current = { surface, surfaceState, visibleCtas }
  const impressions = useRef<{ sessionId?: string; ctas: Set<string> }>({
    ctas: new Set(),
  })
  const track = useCallback((): void => {
    const state = current.current
    if (!state.surface) return
    trackPublicSurface(state.surface, state.surfaceState)
    const sessionId = getPublicActionSessionId()
    if (!sessionId) return
    if (impressions.current.sessionId !== sessionId)
      impressions.current = { sessionId, ctas: new Set() }
    for (const cta of state.visibleCtas) {
      if (!impressions.current.ctas.has(cta) && trackPublicCtaViewed(cta))
        impressions.current.ctas.add(cta)
    }
  }, [])
  useEffect(() => {
    let active = true
    const trackWhenActive = (): void => {
      if (active) track()
    }
    const withdraw = (event: Event): void => {
      const result = ConsentUpdate.safeParse(
        event instanceof CustomEvent ? event.detail : undefined,
      )
      if (result.success && result.data.rejected?.includes('analytics'))
        withdrawPublicTracking()
    }
    document.addEventListener('cookieyes_consent_update', withdraw, true)
    const banner = (event: Event): void => {
      const result = z
        .object({ categories: z.object({ analytics: z.boolean() }) })
        .safeParse(event instanceof CustomEvent ? event.detail : undefined)
      if (result.success && !result.data.categories.analytics)
        clearPublicTracking()
    }
    document.addEventListener('cookieyes_banner_load', banner, true)
    if (window.getCkyConsent?.().categories?.analytics === false)
      clearPublicTracking()
    const cancelActivation = runWhenActivated(document, () => {
      if (isAnalyticsConsented()) trackWhenActive()
      else whenAnalyticsConsented(trackWhenActive)
    })
    return (): void => {
      active = false
      cancelActivation?.()
      document.removeEventListener('cookieyes_consent_update', withdraw, true)
      document.removeEventListener('cookieyes_banner_load', banner, true)
    }
  }, [track])
  useEffect(() => {
    track()
  }, [surface, surfaceState, visibleCtas, track])
}
